/**
 * L4 member access — permissions + associations (AppSync).
 */

import { generateClient } from 'aws-amplify/api'
import type {
  MemberLink,
  MemberLinkPermissions,
  MemberPermissionTemplateId,
} from '@shared/member-links/types'
import type { RecordingConsentAuthorityKind } from '@shared/sessions/session-followers'

const client = generateClient()

export type UserAssociation = {
  peerUserId: string | null
  peerEmail: string | null
  peerDisplayName: string | null
  relationship: string
  relationshipName: string | null
  linkId: string | null
  resolved: boolean
  /** Derived from spoke Independent account permissions (Member reverse-mirror). */
  peerMessagingEnabled?: boolean | null
  peerCallsEnabled?: boolean | null
  /**
   * Effective session follow for this Independent ↔ Member pair (saved, or the
   * relationship default). Null when not applicable.
   */
  sessionsRead?: boolean | null
  sessionNotifications?: boolean | null
  /** True when a Provider Owner saved it (vs the relationship default). */
  sessionFollowExplicit?: boolean | null
  /** Provider Owner "Can give consent for {member}" (recording consent in their app). Null when not applicable. */
  canGiveRecordingConsent?: boolean | null
  /** With the mark: parent or guardian, or someone else (18+ only); null when not chosen yet (counts as someone else). */
  recordingConsentAuthority?: RecordingConsentAuthority | null
}

/** The Provider Owner's choice when marking "Can give consent". */
export type RecordingConsentAuthority = RecordingConsentAuthorityKind

export type AssociationRecordingConsentInput = {
  peerUserId?: string
  peerEmail?: string
  canGiveConsent: boolean
  /** Required with `canGiveConsent: true`. */
  authority?: RecordingConsentAuthority
}

export type AssociationSessionFollowInput = {
  peerUserId?: string
  peerEmail?: string
  sessions: 'read' | 'none'
  sessionNotifications: boolean
}

export type MemberAccess = {
  userId: string
  accountLink: MemberLink | null
  permissions: MemberLinkPermissions
  associations: UserAssociation[]
}

const MEMBER_LINK_FIELDS = /* GraphQL */ `
  linkId
  ownerUserId
  memberUserId
  independentUserId
  inviteEmail
  linkType
  status
  associationKind
  permissions {
    sessions
    messaging
    forum
    calls
    brunoAi
  }
  createdAt
  updatedAt
`

const ASSOCIATION_FIELDS = /* GraphQL */ `
  peerUserId
  peerEmail
  peerDisplayName
  relationship
  relationshipName
  linkId
  resolved
  peerMessagingEnabled
  peerCallsEnabled
`

const ACCESS_ASSOCIATION_FIELDS = /* GraphQL */ `
  ${ASSOCIATION_FIELDS}
  sessionsRead
  sessionNotifications
  sessionFollowExplicit
  canGiveRecordingConsent
  recordingConsentAuthority
`

const GET_MEMBER_ACCESS = /* GraphQL */ `
  query GetMemberAccess($userId: ID!) {
    getMemberAccess(userId: $userId) {
      userId
      accountLink {
        ${MEMBER_LINK_FIELDS}
      }
      permissions {
        sessions
        messaging
        forum
        calls
        brunoAi
      }
      associations {
        ${ACCESS_ASSOCIATION_FIELDS}
      }
    }
  }
`

const MY_ACCOUNT_PERMISSIONS = /* GraphQL */ `
  query MyAccountPermissions {
    myAccountPermissions {
      sessions
      messaging
      forum
      calls
      brunoAi
    }
  }
`

const UPDATE_MEMBER_PERMISSIONS = /* GraphQL */ `
  mutation UpdateMemberPermissions($input: UpdateMemberPermissionsInput!) {
    updateMemberPermissions(input: $input) {
      ${MEMBER_LINK_FIELDS}
    }
  }
`

const SET_MEMBER_ASSOCIATIONS = /* GraphQL */ `
  mutation SetMemberAssociations($input: SetMemberAssociationsInput!) {
    setMemberAssociations(input: $input) {
      ${ASSOCIATION_FIELDS}
    }
  }
`

function unwrap<T>(res: { data?: Record<string, T>; errors?: { message: string }[] }, field: string): T {
  if (res.errors?.length) {
    throw new Error(res.errors.map((e) => e.message).join('; '))
  }
  const value = res.data?.[field]
  if (value === undefined || value === null) throw new Error(`${field} failed`)
  return value
}

export async function getMemberAccess(userId: string): Promise<MemberAccess> {
  const res = (await client.graphql({
    query: GET_MEMBER_ACCESS,
    variables: { userId },
  })) as { data?: { getMemberAccess?: MemberAccess }; errors?: { message: string }[] }
  return unwrap(res as { data?: Record<string, MemberAccess> }, 'getMemberAccess')
}

export async function getMyAccountPermissions(): Promise<MemberLinkPermissions> {
  const res = (await client.graphql({
    query: MY_ACCOUNT_PERMISSIONS,
  })) as {
    data?: { myAccountPermissions?: MemberLinkPermissions }
    errors?: { message: string }[]
  }
  return unwrap(res as { data?: Record<string, MemberLinkPermissions> }, 'myAccountPermissions')
}

export async function updateMemberPermissions(input: {
  userId: string
  permissionTemplate?: MemberPermissionTemplateId
  permissions?: MemberLinkPermissions
  associationSessionFollow?: AssociationSessionFollowInput
  associationRecordingConsent?: AssociationRecordingConsentInput
}): Promise<MemberLink> {
  const res = (await client.graphql({
    query: UPDATE_MEMBER_PERMISSIONS,
    variables: { input },
  })) as { data?: { updateMemberPermissions?: MemberLink }; errors?: { message: string }[] }
  return unwrap(res as { data?: Record<string, MemberLink> }, 'updateMemberPermissions')
}

/**
 * L4: set "Can view sessions" / "Gets session notifications" on one of
 * `userId`'s associations (rides on `updateMemberPermissions`).
 */
export async function updateAssociationSessionFollow(input: {
  userId: string
  follow: AssociationSessionFollowInput
}): Promise<MemberLink> {
  return updateMemberPermissions({ userId: input.userId, associationSessionFollow: input.follow })
}

/** L4: set "Can give consent for {member}" on one of `userId`'s associations. */
export async function updateAssociationRecordingConsent(input: {
  userId: string
  consent: AssociationRecordingConsentInput
}): Promise<MemberLink> {
  return updateMemberPermissions({ userId: input.userId, associationRecordingConsent: input.consent })
}

export async function setMemberAssociations(input: {
  userId: string
  associations: Array<{
    peerUserId?: string
    peerEmail?: string
    relationship: string
    relationshipName?: string
  }>
}): Promise<UserAssociation[]> {
  const res = (await client.graphql({
    query: SET_MEMBER_ASSOCIATIONS,
    variables: { input },
  })) as { data?: { setMemberAssociations?: UserAssociation[] }; errors?: { message: string }[] }
  return unwrap(res as { data?: Record<string, UserAssociation[]> }, 'setMemberAssociations')
}

/**
 * L4 "Resync from AHP": re-apply the contacts AHP last sent us (an
 * Independent's own contacts; for a Member, every Independent listing them).
 * Nothing new is downloaded from AHP. Returns the refreshed associations.
 */
export async function resyncMemberAssociations(userId: string): Promise<UserAssociation[]> {
  const res = (await client.graphql({
    query: SET_MEMBER_ASSOCIATIONS,
    variables: { input: { userId, resync: true } },
  })) as { data?: { setMemberAssociations?: UserAssociation[] }; errors?: { message: string }[] }
  return unwrap(res as { data?: Record<string, UserAssociation[]> }, 'setMemberAssociations')
}
